import React, { useState, useRef } from 'react';
import { Mail, Server, Lock, User, Check, X, Loader, ChevronRight, RefreshCw, AlertCircle, Info, Eye, EyeOff } from 'lucide-react';
import './EmailInbox.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PROVIDERS = [
  {
    name: 'Gmail',
    emoji: '✉️',
    imapHost: 'imap.gmail.com', imapPort: 993, imapSecure: true,
    smtpHost: 'smtp.gmail.com', smtpPort: 587, smtpSecure: false
  },
  {
    name: 'Outlook',
    emoji: '📧',
    imapHost: 'outlook.office365.com', imapPort: 993, imapSecure: true,
    smtpHost: 'smtp.office365.com', smtpPort: 587, smtpSecure: false
  },
  {
    name: 'Yahoo',
    emoji: '📮',
    imapHost: 'imap.mail.yahoo.com', imapPort: 993, imapSecure: true,
    smtpHost: 'smtp.mail.yahoo.com', smtpPort: 465, smtpSecure: true
  },
  {
    name: 'Custom',
    emoji: '⚙️',
    imapHost: '', imapPort: 993, imapSecure: true,
    smtpHost: '', smtpPort: 587, smtpSecure: false
  }
];

const INITIAL_FORM = {
  label: '',
  emailAddress: '',
  fromName: '',
  password: '',
  imapHost: '',
  imapPort: 993,
  imapSecure: true,
  smtpHost: '',
  smtpPort: 587,
  smtpSecure: false
};

export default function ConnectAccountModal({ onClose, onConnected }) {
  const token = localStorage.getItem('crm_token');
  const [form, setForm]                 = useState(INITIAL_FORM);
  const [selectedProvider, setProvider] = useState(null);
  const [step, setStep]                 = useState(1);  // 1=provider select, 2=credentials
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState(false);
  const [syncStatus, setSyncStatus]     = useState('');  // 'syncing' | 'done' | 'failed'
  const [showPassword, setShowPassword] = useState(false);
  const errorRef                        = useRef(null);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const selectProvider = (p) => {
    setProvider(p.name);
    setForm(prev => ({
      ...prev,
      imapHost: p.imapHost,
      imapPort: p.imapPort,
      imapSecure: p.imapSecure,
      smtpHost: p.smtpHost,
      smtpPort: p.smtpPort,
      smtpSecure: p.smtpSecure,
      // Auto-fill label if not already set
      label: prev.label || p.name
    }));
    setStep(2);
  };

  const handleConnect = async () => {
    setError('');

    // Validate required fields
    const missing = [];
    if (!form.emailAddress) missing.push('Email Address');
    if (!form.password)     missing.push('Password');
    if (!form.imapHost)     missing.push('IMAP Host');
    if (!form.smtpHost)     missing.push('SMTP Host');

    if (missing.length > 0) {
      setError(`Please fill in: ${missing.join(', ')}`);
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/email-inbox/connect`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          imapPort: parseInt(form.imapPort) || 993,
          smtpPort: parseInt(form.smtpPort) || 587
        })
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Connection failed. Please check your credentials.');
      }

      // Account saved — now trigger manual sync so emails load quickly
      setSuccess(true);
      setSyncStatus('syncing');

      try {
        await fetch(`${API}/email-inbox/accounts/${data.account._id}/sync`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ folder: 'INBOX', limit: 50 })
        });
        setSyncStatus('done');
      } catch (_) {
        setSyncStatus('failed');
      }

      // Give user a moment to see success before closing
      setTimeout(() => onConnected(data.account), 1500);
    } catch (e) {
      setError(e.message);
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ei-overlay" onClick={(e) => e.target === e.currentTarget && !loading && onClose()}>
      <div className="ei-modal" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="ei-modal-header">
          <h2 className="ei-modal-title">
            <Mail size={20} color="#6c63ff" />
            {step === 1 ? 'Choose Email Provider' : 'Connect Email Account'}
          </h2>
          <button className="ei-icon-btn" onClick={onClose} disabled={loading}>
            <X size={18} />
          </button>
        </div>

        <div className="ei-modal-body">
          {/* Error alert — scrolled to by ref */}
          <div ref={errorRef}>
            {error && (
              <div className="ei-alert-error">
                <AlertCircle size={16} /> {error}
              </div>
            )}
          </div>

          {/* Success state */}
          {success && (
            <div className="ei-alert-success">
              <Check size={18} />
              <div>
                <div style={{ fontWeight: 600 }}>Account connected!</div>
                <div style={{ fontSize: 12, marginTop: 2 }}>
                  {syncStatus === 'syncing' && 'Syncing your inbox…'}
                  {syncStatus === 'done'    && 'Inbox synced. Loading your emails…'}
                  {syncStatus === 'failed'  && 'Saved! Sync will happen shortly in the background.'}
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 1: Provider Selection ───────────────────────────── */}
          {step === 1 && (
            <>
              <p style={{ color: '#888899', fontSize: '14px', marginBottom: '20px', lineHeight: '1.5' }}>
                Choose your email provider. For Gmail/Yahoo you'll need an <strong style={{ color: '#f59e0b' }}>App Password</strong> — not your normal login password.
              </p>

              <div className="ei-provider-grid">
                {PROVIDERS.map(p => (
                  <button
                    key={p.name}
                    className={`ei-provider-btn ${selectedProvider === p.name ? 'selected' : ''}`}
                    onClick={() => selectProvider(p)}
                  >
                    <span className="ei-provider-emoji">{p.emoji}</span>
                    <span>{p.name}</span>
                    <ChevronRight size={12} />
                  </button>
                ))}
              </div>

              {/* Gmail tip */}
              <div style={{
                background: 'rgba(245,158,11,0.08)',
                border: '1px solid rgba(245,158,11,0.2)',
                borderRadius: 10,
                padding: '12px 16px',
                fontSize: 13,
                color: '#fbbf24',
                lineHeight: 1.6
              }}>
                <strong>📌 Gmail users:</strong> Go to Google Account → Security → 2-Step Verification → App Passwords. Generate one for "Mail" and use it as your password here.
              </div>
            </>
          )}

          {/* ── STEP 2: Credentials ──────────────────────────────────── */}
          {step === 2 && !success && (
            <>
              <button
                onClick={() => { setStep(1); setError(''); }}
                style={{ background: 'none', border: 'none', color: '#6c63ff', cursor: 'pointer', fontSize: 13, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 4 }}
              >
                ← Back to provider selection
              </button>

              {/* Account Info */}
              <div className="ei-section-title"><User size={14} /> Account Info</div>

              <div className="ei-form-group">
                <label className="ei-form-label">Account Label</label>
                <input
                  className="ei-form-input"
                  placeholder="e.g. My Work Email"
                  value={form.label}
                  onChange={e => set('label', e.target.value)}
                />
              </div>

              <div className="ei-form-row">
                <div className="ei-form-group">
                  <label className="ei-form-label">Email Address *</label>
                  <input
                    className="ei-form-input"
                    type="email"
                    placeholder="you@example.com"
                    value={form.emailAddress}
                    onChange={e => set('emailAddress', e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <div className="ei-form-group">
                  <label className="ei-form-label">Display Name</label>
                  <input
                    className="ei-form-input"
                    placeholder="Your Name"
                    value={form.fromName}
                    onChange={e => set('fromName', e.target.value)}
                  />
                </div>
              </div>

              <div className="ei-form-group">
                <label className="ei-form-label">
                  <Lock size={12} style={{ display: 'inline', marginRight: 4 }} />
                  Password / App Password *
                </label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    className="ei-form-input"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Use App Password for Gmail/Yahoo"
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                    autoComplete="current-password"
                    style={{ paddingRight: '40px' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      background: 'none',
                      border: 'none',
                      color: '#8888aa',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px'
                    }}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* IMAP Settings */}
              <div className="ei-section-title"><Server size={14} /> IMAP Settings (Incoming Mail)</div>

              <div className="ei-form-row">
                <div className="ei-form-group">
                  <label className="ei-form-label">IMAP Host *</label>
                  <input
                    className="ei-form-input"
                    placeholder="imap.example.com"
                    value={form.imapHost}
                    onChange={e => set('imapHost', e.target.value)}
                  />
                </div>
                <div className="ei-form-group">
                  <label className="ei-form-label">IMAP Port</label>
                  <input
                    className="ei-form-input"
                    type="number"
                    value={form.imapPort}
                    onChange={e => set('imapPort', e.target.value)}
                  />
                </div>
              </div>
              <label className="ei-form-toggle">
                <input
                  type="checkbox"
                  checked={form.imapSecure}
                  onChange={e => set('imapSecure', e.target.checked)}
                />
                Use SSL/TLS for IMAP (port 993)
              </label>

              {/* SMTP Settings */}
              <div className="ei-section-title" style={{ marginTop: 20 }}><Mail size={14} /> SMTP Settings (Outgoing Mail)</div>

              <div className="ei-form-row">
                <div className="ei-form-group">
                  <label className="ei-form-label">SMTP Host *</label>
                  <input
                    className="ei-form-input"
                    placeholder="smtp.example.com"
                    value={form.smtpHost}
                    onChange={e => set('smtpHost', e.target.value)}
                  />
                </div>
                <div className="ei-form-group">
                  <label className="ei-form-label">SMTP Port</label>
                  <input
                    className="ei-form-input"
                    type="number"
                    value={form.smtpPort}
                    onChange={e => set('smtpPort', e.target.value)}
                  />
                </div>
              </div>
              <label className="ei-form-toggle">
                <input
                  type="checkbox"
                  checked={form.smtpSecure}
                  onChange={e => set('smtpSecure', e.target.checked)}
                />
                Use SSL/TLS for SMTP (port 465)
              </label>

              {/* Info note */}
              <div style={{
                background: 'rgba(108,99,255,0.08)',
                border: '1px solid rgba(108,99,255,0.2)',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: 12,
                color: '#a78bfa',
                marginTop: 16,
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start'
              }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                Your account will be saved immediately. The inbox will sync in the background — this may take a few seconds depending on your mail server.
              </div>
            </>
          )}
        </div>

        {/* Footer — only show when on step 2 and not already succeeded */}
        {step === 2 && !success && (
          <div className="ei-modal-footer">
            <button className="ei-btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button
              className="ei-btn-primary"
              onClick={handleConnect}
              disabled={loading}
              id="ei-connect-btn"
            >
              {loading ? (
                <><Loader size={16} className="ei-spinner" /> Saving account…</>
              ) : (
                <><Check size={16} /> Connect Account</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
